import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchReviews } from '../../store/slices/review.slice'
import { Link } from 'react-router-dom'
import { getAnimeDetailsUrl, getAnimeImage } from '../../services/util.service'
import { postListNewImages } from '../../services/images.service'
import { loadAnimesByIds } from '../../controllers/animeDB.controller.js'

const REVIEWS_PER_PAGE = 10 // כמות טעינות בכל לחיצה

export function UserReviews({ userId }) {
  const dispatch = useDispatch()
  const [currentPage, setCurrentPage] = useState(1)
  const [animeMap, setAnimeMap] = useState({})
  const [isLoadingAnimes, setIsLoadingAnimes] = useState(false)

  const reviews = useSelector((state) => state.reviewModule.reviews) || []
  const userReviews = useMemo(
    () => reviews.filter((review) => review.userId === userId),
    [reviews, userId]
  )

  const displayedReviews = useMemo(() => {
    return userReviews.slice(0, currentPage * REVIEWS_PER_PAGE)
  }, [userReviews, currentPage])

  const animeIds = useMemo(() => {
    const ids = displayedReviews.map((review) => Number(review.animeId))
    return [...new Set(ids)]
  }, [displayedReviews])

  useEffect(() => {
    if (userId) {
      dispatch(fetchReviews({ userId }))
    }
  }, [dispatch, userId])

  useEffect(() => {
    setIsLoadingAnimes(true)

    loadAnimesByIds(animeIds)
      .then((animes) => {
        setAnimeMap((prevAnimeMap) => {
          const newAnimeMap = { ...prevAnimeMap }
          animes.forEach((anime) => {
            newAnimeMap[anime.mal_id] = anime
          })
          return newAnimeMap
        })
      })
      .catch((err) => {
        console.error('❌ Error loading animes by IDs:', err)
      })
      .finally(() => {
        setIsLoadingAnimes(false)
      })
  }, [animeIds])

  const handleLoadMore = () => {
    setCurrentPage((prevPage) => prevPage + 1)
  }

  if (!userReviews.length) {
    return <div className="text-white p-4">לא נמצאו ביקורות</div>
  }

  return (
    <div className="container mx-auto">
      {displayedReviews.map((review) => {
        const anime = animeMap[Number(review.animeId)]
        return (
          <React.Fragment key={review._id}>
            <div className="p-4 mb-4 flex flex-col lg:flex-row gap-4 items-center bg-secondaryBackground">
              <div className="w-[200px] h-[280px]">
                {anime ? (
                  <Link
                    to={getAnimeDetailsUrl(anime)}
                    className="block w-full h-full"
                  >
                    <img
                      src={getAnimeImage(anime)}
                      alt={anime.title}
                      className="w-full h-full object-cover"
                    />
                  </Link>
                ) : (
                  <div className="w-full h-full bg-gray-300 flex items-center justify-center text-black">
                    אין תמונה
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 items-center text-center lg:items-start lg:text-right justify-between">
                {anime ? (
                  <Link
                    to={getAnimeDetailsUrl(anime)}
                    className="hover:underline"
                  >
                    <div>
                      <h1 className="text-white text-4xl">
                        {anime.title_hebrew || anime.title_english}
                      </h1>
                      <h3 className="text-xl">
                        {anime.title_english || anime.title}
                      </h3>
                    </div>
                  </Link>
                ) : (
                  <div className="text-white text-xl">פרטי אנימה לא זמינים</div>
                )}
                <div className="w-full flex flex-col gap-8">
                  <p className="text-white text-lg lg:w-[900px]">
                    {review.content}
                  </p>
                  <div className="flex flex-col gap-3">
                    <div className="flex justify-center lg:justify-start items-center gap-3">
                      {review.recommended !== null && (
                        <span className="text-buttonSecondary1 font-semibold">
                          {review.recommended ? 'ממליץ' : 'לא ממליץ'}
                        </span>
                      )}
                      <span className="bg-[#464646] w-1 h-1 rounded-full"></span>
                      <span className="text-buttonSecondary1 font-semibold">
                        דירוג: {review.rating}/10
                      </span>
                    </div>
                    <p className="text-muted text-sm">
                      תאריך יצירה:{' '}
                      {new Date(review.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <img
              className="w-full my-10"
              src={postListNewImages.longLineDivider}
              alt="Divider"
            />
          </React.Fragment>
        )
      })}

      {displayedReviews.length < userReviews.length && (
        <div className="flex justify-center mt-6">
          <button
            style={{
              background: `url(${postListNewImages.blockBtn}) no-repeat center/contain`,
            }}
            className="px-4 py-2 w-[130px] h-[40px] text-buttonSecondary1"
            onClick={handleLoadMore}
          >
            טען עוד
          </button>
        </div>
      )}
    </div>
  )
}
